import * as S from './styles';
import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { authInfoSelect } from '@entities/auth';
import { AppDispatch } from '@config/store';
import { getAtsUsers, getCalls } from '@api/thunks/booking';
import { bookingSelect } from '@entities/booking';
import { Table } from 'antd';
import { rKeeperSelect } from '@entities/rKeeper';
import { getCallColumns } from '@pages/booking/calls/ui/callsColumns';
import CustomDatePicker from '@components/datePicker';
import { IPaginatedProps } from '@types';
import { limitCardChanges, limitCardTrans } from '@utils/const';
import { useClientActions } from '@hooks/useClientActions';
import { useClientData } from '@hooks/useClientData';
import { cardSelect } from '@entities/card';
import ClientModal from '@pages/crm/components/ClientModal';
import { CustomPlayer } from '@pages/booking/calls/ui/player';
import { ICalls } from '@entities/booking/types';

const CallsPage: FC = () => {
    const { shop } = useSelector(authInfoSelect);
    const [transPagination, setTransPagination] = useState<IPaginatedProps>({
        page: 1,
        limit: limitCardTrans,
    });
    const [changesPagination, setChangesPagination] = useState<IPaginatedProps>(
        {
            page: 1,
            limit: limitCardChanges,
        }
    );
    const [openedSettingsPopup, setOpenedSettingsPopup] =
        useState<boolean>(false);
    const [selectedCall, setSelectedCall] = useState<ICalls | null>(null);
    const [showPlayer, setShowPlayer] = useState<boolean>(false);
    const { calls, atsUsersData, loading, tableParams } =
        useSelector(bookingSelect);
    const { filters } = tableParams;
    const dispatch = useDispatch<AppDispatch>();
    const { lastShiftClosed } = useSelector(rKeeperSelect);
    const { transactionCount, changesCount } = useSelector(cardSelect);

    const { handleShowClientModal, handlerClickId } = useClientActions({
        transPagination,
        changesPagination,
        setOpenedSettingsPopup,
    });
    useClientData(transPagination, changesPagination);

    const handleRecordClick = (call: ICalls) => {
        setSelectedCall(call);
        setShowPlayer(true);
        setTimeout(() => {
            window.scrollTo({
                top: document.documentElement.scrollHeight,
                behavior: 'smooth',
            });
        }, 100);
    };

    const loadMore = () => {
        if (transactionCount > transPagination.page * transPagination.limit) {
            const newPage = transPagination.page + 1;
            setTransPagination(prev => ({
                ...prev,
                page: newPage,
            }));
        }
    };

    const loadMoreCardChanges = () => {
        if (changesCount > changesPagination.page * changesPagination.limit) {
            const newPage = changesPagination.page + 1;

            setChangesPagination(prev => ({
                ...prev,
                page: newPage,
            }));
        }
    };

    useEffect(() => {
        const params = {};
        if (shop) {
            dispatch(getAtsUsers({ shop_id: shop.id, params }));
        }
    }, [shop, dispatch]);

    useEffect(() => {
        if (shop && lastShiftClosed) {
            const params = {
                date:
                    Object.keys(filters).length > 0
                        ? (filters.date as unknown as string)
                        : lastShiftClosed.SHIFTDATE,
            };
            dispatch(getCalls({ shop_id: shop.id, params }));
        }
    }, [shop, dispatch, filters, lastShiftClosed]);

    const columns = getCallColumns(
        atsUsersData,
        shop?.id,
        dispatch,
        handlerClickId,
        handleRecordClick
    );

    return (
        <S.Wrapper>
            <S.HeaderWrapper>
                <S.Title>Звонки</S.Title>
                <S.StyledCalendarWrapper>
                    <CustomDatePicker />
                </S.StyledCalendarWrapper>
            </S.HeaderWrapper>
            <S.TableWrapper>
                <Table
                    size="small"
                    columns={columns}
                    rowKey={rec => rec.date}
                    dataSource={calls}
                    scroll={{ x: 700 }}
                    loading={loading === 'loading'}
                />
            </S.TableWrapper>

            {showPlayer && selectedCall && (
                <CustomPlayer
                    source={
                        'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3'
                    }
                    date={selectedCall.date}
                    phone={selectedCall.phone}
                />
            )}
            <S.Overlay display={openedSettingsPopup ? 'block' : 'none'} />
            {openedSettingsPopup && (
                <ClientModal
                    loadMoreCardChanges={loadMoreCardChanges}
                    loadMore={loadMore}
                    setOpeningSettingsPopup={handleShowClientModal}
                />
            )}
        </S.Wrapper>
    );
};
export default CallsPage;
